import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import {
  getDefaultLateTime,
  formatStudentName as formatStudentNameBase,
} from "../utils/attendanceHelpers";
import { stundenplanQueryApi } from "../../klassenbuch/stundenplan/api/queries";
import type { TempAttendanceMap } from "../types/attendanceDialog";

// ============================================================================
// Types
// ============================================================================

interface UseAttendanceActionsParams {
  selectedLesson: any;
  overviewData: any;
  tempAttendance: TempAttendanceMap;
  setTempAttendance: React.Dispatch<React.SetStateAction<TempAttendanceMap>>;
  setEditedStudentIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setAttendanceViewMode: (mode: "overview" | "edit") => void;
  setLessonNote: (note: string) => void;
  protectionByStudent: Record<string, any>;
  explicitPresentIdsRef: React.MutableRefObject<Set<string>>;
  isMobile: boolean;
  // Transfer
  lessonId?: string;
  transferReason: string;
  transferStudent: any;
  reloadBundle: () => Promise<void>;
  setPendingTransfer: (v: any) => void;
  setTransferReason: (v: string) => void;
  setTransferredStudentIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

// ============================================================================
// Hook
// ============================================================================

export function useAttendanceActions({
  selectedLesson,
  overviewData,
  tempAttendance,
  setTempAttendance,
  setEditedStudentIds,
  setAttendanceViewMode,
  setLessonNote,
  protectionByStudent,
  explicitPresentIdsRef,
  isMobile,
  lessonId,
  transferReason,
  transferStudent,
  reloadBundle,
  setPendingTransfer,
  setTransferReason,
  setTransferredStudentIds,
}: UseAttendanceActionsParams) {
  const dispatch = useDispatch();

  // Invalidate the stundenplan week-bundle cache so the grid refetches
  // its student_count / attendance_count for every tile after a transfer.
  // Without this the source-cell tile keeps the stale denominator from the
  // initial page load (the transferred student was excluded as concurrent-
  // course → now counted) and renders as ⚠ "incomplete" until the user
  // reloads the page manually.
  const invalidateStundenplanWeek = useCallback(() => {
    dispatch(
      stundenplanQueryApi.util.invalidateTags([
        { type: "Stundenplan", id: "WEEK" },
      ]),
    );
  }, [dispatch]);

  // ──────────────────────────────────────────────────────────────────────
  // formatStudentName — closure wrapper over isMobile + is_course
  // ──────────────────────────────────────────────────────────────────────
  const formatStudentName = useCallback(
    (fullName: string) =>
      formatStudentNameBase(
        fullName,
        isMobile,
        !!(selectedLesson as any)?.is_course,
      ),
    [isMobile, selectedLesson],
  );

  // ──────────────────────────────────────────────────────────────────────
  // setStudentStatus — set an individual student's attendance status
  // ──────────────────────────────────────────────────────────────────────
  const setStudentStatus = useCallback(
    (
      studentId: string,
      status: "present" | "late" | "excused" | "unexcused",
    ) => {
      setEditedStudentIds((prev) => new Set([...prev, studentId]));
      setTempAttendance((prev) => {
        const current = prev[studentId];
        let minutesLate: number | undefined;
        let arrivalTime: string | undefined;
        let lateExcused: boolean | undefined;
        if (status === "late") {
          arrivalTime =
            current?.arrivalTime || getDefaultLateTime(selectedLesson);
          minutesLate = current?.minutesLate || 5;
          lateExcused = current?.lateExcused || false;
        }
        return {
          ...prev,
          [studentId]: {
            status,
            minutesLate,
            arrivalTime,
            lateExcused,
            excuseReason:
              status === "excused" ? current?.excuseReason || "" : undefined,
          },
        };
      });
    },
    [selectedLesson, setEditedStudentIds, setTempAttendance],
  );

  // ──────────────────────────────────────────────────────────────────────
  // setAllStudentsStatus — mark all (non-protected) students at once
  // ──────────────────────────────────────────────────────────────────────
  const setAllStudentsStatus = useCallback(
    (status: "present" | "late" | "excused" | "unexcused") => {
      if (!selectedLesson) return;
      const newAttendance: TempAttendanceMap = {};
      selectedLesson.students?.forEach((student: any) => {
        const prot =
          protectionByStudent[student.id] || ({ protected: false } as any);
        if (prot.protected) return;
        let minutesLate: number | undefined;
        let arrivalTime: string | undefined;
        let lateExcused: boolean | undefined;
        if (status === "late") {
          arrivalTime = getDefaultLateTime(selectedLesson);
          minutesLate = 5;
          lateExcused = false;
        }
        newAttendance[student.id] = {
          status,
          minutesLate,
          arrivalTime,
          lateExcused,
          excuseReason: status === "excused" ? "" : undefined,
        };
      });
      setTempAttendance((prev) => ({ ...prev, ...newAttendance }));
    },
    [selectedLesson, protectionByStudent, setTempAttendance],
  );

  // ──────────────────────────────────────────────────────────────────────
  // switchToEdit — convert overview snapshot to editable attendance map
  // ──────────────────────────────────────────────────────────────────────
  const switchToEdit = useCallback(() => {
    if (!selectedLesson || !overviewData) return;
    const nameToStudentId = new Map<string, string>();
    selectedLesson.students?.forEach((s: any) => {
      const baseName = (s.name?.split(" (")[0] || "").trim().toLowerCase();
      if (baseName) nameToStudentId.set(baseName, s.id);
    });
    const editAttendance: TempAttendanceMap = {};
    const explicitIds = explicitPresentIdsRef.current;
    overviewData.present?.forEach((student: any) => {
      const key = String(student.name || "")
        .split(" (")[0]
        .trim()
        .toLowerCase();
      const studentId = nameToStudentId.get(key);
      if (studentId && explicitIds.has(studentId))
        editAttendance[studentId] = { status: "present" };
    });
    overviewData.late?.forEach((student: any) => {
      const key = String(student.name || "")
        .split(" (")[0]
        .trim()
        .toLowerCase();
      const studentId = nameToStudentId.get(key);
      if (studentId)
        editAttendance[studentId] = {
          status: "late",
          minutesLate: student.minutesLate || 5,
          arrivalTime:
            student.arrivalTime || getDefaultLateTime(selectedLesson),
          lateExcused: student.lateExcused || false,
        };
    });
    overviewData.absent?.forEach((student: any) => {
      const key = String(student.name || "")
        .split(" (")[0]
        .trim()
        .toLowerCase();
      const studentId = nameToStudentId.get(key);
      if (studentId)
        editAttendance[studentId] = {
          status: student.excused ? "excused" : "unexcused",
          excuseReason: student.reason || "",
        };
    });
    setTempAttendance(editAttendance);
    setLessonNote(overviewData.lessonNote || "");
    setAttendanceViewMode("edit");
  }, [
    selectedLesson,
    overviewData,
    explicitPresentIdsRef,
    setTempAttendance,
    setLessonNote,
    setAttendanceViewMode,
  ]);

  // ──────────────────────────────────────────────────────────────────────
  // handleTransferConfirm — transfer a student from another course's lesson
  // ──────────────────────────────────────────────────────────────────────
  const handleTransferConfirm = useCallback(
    async (studentId: string, fromLessonId: string) => {
      try {
        await transferStudent({
          studentId,
          fromLessonId,
          toLessonId: lessonId!,
          status: "present",
          reason: transferReason,
        }).unwrap();
        setPendingTransfer(null);
        setTransferReason("");
        setTransferredStudentIds((prev) => new Set([...prev, studentId]));
        // Trigger a full bundle reload to refresh overview, badges, and concurrent data
        await reloadBundle();
        invalidateStundenplanWeek();
      } catch (err) {
        console.error("Transfer failed:", err);
      }
    },
    [
      transferStudent,
      lessonId,
      transferReason,
      setPendingTransfer,
      setTransferReason,
      setTransferredStudentIds,
      reloadBundle,
      invalidateStundenplanWeek,
    ],
  );

  // ──────────────────────────────────────────────────────────────────────
  // handleReturnToCourseConfirm — move student from main lesson back to course
  // ──────────────────────────────────────────────────────────────────────
  const handleReturnToCourseConfirm = useCallback(
    async (studentId: string, fromMainLessonId: string) => {
      try {
        await transferStudent({
          studentId,
          fromLessonId: fromMainLessonId,
          toLessonId: lessonId!,
          status: "present",
          reason: "Korrektur: zurück in Kurs",
        }).unwrap();
        setPendingTransfer(null);
        setTransferReason("");
        setTransferredStudentIds((prev) => {
          const next = new Set(prev);
          next.delete(studentId);
          return next;
        });
        await reloadBundle();
        invalidateStundenplanWeek();
      } catch (err) {
        console.error("Return-to-course transfer failed:", err);
      }
    },
    [
      transferStudent,
      lessonId,
      setPendingTransfer,
      setTransferReason,
      setTransferredStudentIds,
      reloadBundle,
      invalidateStundenplanWeek,
    ],
  );

  /** Course edit: child marked present/late on course but stayed with class → main lesson. */
  const handleStayedWithClassTransfer = useCallback(
    async (studentId: string, reason: string): Promise<boolean> => {
      const mainId = (selectedLesson as any)?.parallel_main_lesson_id as
        | string
        | null
        | undefined;
      if (!lessonId || !mainId) return false;
      try {
        await transferStudent({
          studentId,
          fromLessonId: lessonId,
          toLessonId: mainId,
          // null → pending mode: don't auto-mark the student present on
          // the home class lesson. The RPC writes a kind='transferred_back'
          // row to lesson_guest_students instead, and the home class
          // dialog surfaces the student as Ausstehend so the home teacher
          // decides whether they actually made it back.
          status: null,
          reason: reason.trim() || "Bei der Klasse geblieben",
        }).unwrap();
        setEditedStudentIds((prev) => {
          const next = new Set(prev);
          next.delete(studentId);
          return next;
        });
        setTempAttendance((prev) => {
          const next = { ...prev };
          delete next[studentId];
          return next;
        });
        await reloadBundle();
        invalidateStundenplanWeek();
        return true;
      } catch (err) {
        const message = (err as { message?: string })?.message || "";
        if (message.includes("Tagesgast")) {
          toast.error(
            "Dieses Kind ist bereits als Tagesgast in der Klasse eingetragen. Bitte zuerst dort entfernen.",
          );
        } else {
          toast.error(
            "Übertrag in die Klasse fehlgeschlagen. Bitte erneut versuchen.",
          );
        }
        console.error("Stayed-with-class transfer failed:", err);
        return false;
      }
    },
    [
      transferStudent,
      lessonId,
      selectedLesson,
      reloadBundle,
      setEditedStudentIds,
      setTempAttendance,
      invalidateStundenplanWeek,
    ],
  );

  return {
    formatStudentName,
    setStudentStatus,
    setAllStudentsStatus,
    switchToEdit,
    handleTransferConfirm,
    handleReturnToCourseConfirm,
    handleStayedWithClassTransfer,
  };
}
